import { useCallback, useEffect, useRef, useState } from 'react';
import { useClient } from './use-client';
import { toast } from '@cotera/client/app/components';
import { playRecordingStartCue, playRecordingStopCue } from './voice-cues';
import { createAudioContext } from './audio-context';

export const useVoiceRecording = (
  onTranscriptionComplete: (text: string) => void
) => {
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  /**
   * Current input loudness, 0 to 1, sampled from the live stream.
   *
   * The button used to pulse on a fixed timer, which looked identical whether
   * the mic was picking up speech or muted at the OS level. Driving the visual
   * from the actual signal means a silent mic looks silent.
   */
  const [level, setLevel] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const analyserCleanupRef = useRef<(() => void) | null>(null);
  const client = useClient();

  const stopMetering = useCallback(() => {
    analyserCleanupRef.current?.();
    analyserCleanupRef.current = null;
    setLevel(0);
  }, []);

  /** Feeds `level` from the stream until the returned cleanup is called. */
  const startMetering = useCallback((stream: MediaStream) => {
    try {
      const ctx = createAudioContext();
      if (ctx === null) {
        return;
      }
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      ctx.createMediaStreamSource(stream).connect(analyser);

      const samples = new Uint8Array(analyser.frequencyBinCount);
      let frame = 0;

      const tick = (): void => {
        analyser.getByteTimeDomainData(samples);
        // Peak deviation from the 128 midpoint, which tracks speech onsets
        // more responsively than an average.
        let peak = 0;
        for (const sample of samples) {
          peak = Math.max(peak, Math.abs(sample - 128));
        }
        setLevel(Math.min(1, peak / 96));
        frame = requestAnimationFrame(tick);
      };
      frame = requestAnimationFrame(tick);

      analyserCleanupRef.current = () => {
        cancelAnimationFrame(frame);
        void ctx.close();
      };
    } catch {
      // Metering is decoration; recording continues without it.
    }
  }, []);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Check supported MIME types
      const mimeType = MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : MediaRecorder.isTypeSupported('audio/mp4')
        ? 'audio/mp4'
        : '';

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: mimeType || undefined,
      });
      const chunks: BlobPart[] = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };

      mediaRecorder.onstop = async () => {
        if (chunks.length === 0) {
          return;
        }

        const blob = new Blob(chunks, { type: 'audio/webm' });

        if (blob.size === 0) {
          return;
        }

        const formData = new FormData();
        formData.append('audio', blob, 'recording.webm');

        try {
          setTranscribing(true);
          const result = await client.u.transcribe.audio({ formData });

          if (result.isErr()) {
            throw new Error(
              `Transcription failed: ${result.error.err.errorType}`
            );
          }

          if (result.value.text !== '') {
            onTranscriptionComplete(result.value.text);
          }
        } finally {
          setTranscribing(false);
        }
      };

      mediaRecorderRef.current = mediaRecorder;
      // Request data every 100ms to ensure we capture chunks
      mediaRecorder.start(100);
      startMetering(stream);
      playRecordingStartCue();
      setRecording(true);
    } catch (error) {
      // Previously swallowed, which meant a denied permission looked exactly
      // like a dead button: nothing happened and nothing said why.
      const denied =
        error instanceof DOMException &&
        (error.name === 'NotAllowedError' || error.name === 'SecurityError');
      toast.error(
        denied
          ? 'Microphone access is blocked. Allow it in your browser settings to dictate.'
          : 'Could not start recording. Check that a microphone is connected.'
      );
    }
  }, [onTranscriptionComplete, client, startMetering]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream
        .getTracks()
        .forEach((track) => track.stop());
      stopMetering();
      playRecordingStopCue();
      setRecording(false);
    }
  }, [recording, stopMetering]);

  // Leaving the page mid-recording would otherwise leave the meter's audio
  // context and animation frame running.
  useEffect(() => stopMetering, [stopMetering]);

  const toggleRecording = useCallback(async () => {
    if (recording) {
      stopRecording();
    } else {
      await startRecording();
    }
  }, [recording, startRecording, stopRecording]);

  return {
    recording,
    transcribing,
    level,
    toggleRecording,
  };
};
